import React, { useEffect, memo, useCallback } from 'react';
import { useQueryBasedViewAutomaticTracking } from '@atlassian/jira-business-experience-tracking/src/controllers/experience-tracker/index.tsx';
import { useFilteredViewContextInput } from '@atlassian/jira-business-filters/src/controllers/default-query-input/index.tsx';
import type { ChildQueryControllerProps } from '@atlassian/jira-business-list-handlers/src/controllers/children/types.tsx';
import { getAGGCompliantData } from '@atlassian/jira-business-list-handlers/src/controllers/pagination/utils.tsx';
import { ErrorFlag } from '@atlassian/jira-issue-error-flag';
import { fireTrackAnalytics, useAnalyticsEvents } from '@atlassian/jira-product-analytics-bridge';
import { LOAD_CHILDREN_EXPERIENCE } from '../../common/constants';
import { useListViewIssues as useListViewIssuesService } from '../../services/get-list-view-issues';
import type { JWMListViewItems } from '../../services/get-list-view-issues/__generated_apollo__/JWMListViewItems';
import type { ListViewIssuesReturn } from '../../services/get-list-view-issues/types';
import { useIssueFields } from '../get-issue-fields';
import messages from './messages';

type ListViewChildQueryControllerProps = ChildQueryControllerProps<ListViewIssuesReturn>;

export const ListViewChildQueryController = memo<ListViewChildQueryControllerProps>(
	({ parentHierarchyType, parentIssueId, onDataChange, parentHierarchyLevel }) => {
		const { createAnalyticsEvent } = useAnalyticsEvents();
		const { jqlContext, quickFiltersJql, sorting, isHidingDoneItems } = useFilteredViewContextInput(
			{
				view: 'list',
			},
		);
		const {
			getQueryFieldIds,
			loading: isLoadingFields,
			isParentFieldVisible,
			isDevSummaryFieldVisible,
		} = useIssueFields();

		const onComplete = useCallback(
			(responseData: JWMListViewItems) => {
				fireTrackAnalytics(createAnalyticsEvent({}), 'listChildrenData fetched', {
					totalChildren: getAGGCompliantData(responseData, 'list')?.edges?.length,
					parentIssueId,
					hierarchyLevel: parentHierarchyLevel,
				});
			},
			[createAnalyticsEvent, parentHierarchyLevel, parentIssueId],
		);

		const { data, error, hasNextPage, isLoadingNextPage, loading, loadNextPage } =
			useListViewIssuesService({
				input: {
					jqlContext,
					fieldIds: getQueryFieldIds(),
					quickFiltersJql,
					parentIssue: {
						hierarchyType: parentHierarchyType,
						hierarchyLevel: parentHierarchyLevel,
						issueId: parentIssueId,
					},
					sorting,
					isHidingDoneItems,
					isParentFieldVisible: isParentFieldVisible(),
					isDevSummaryFieldVisible: isDevSummaryFieldVisible(),
				},
				condition: !isLoadingFields,
				onComplete,
			});

		useQueryBasedViewAutomaticTracking(LOAD_CHILDREN_EXPERIENCE, {
			loading,
			error,
			shouldStartExperience: true,
		});

		useEffect(() => {
			onDataChange(parentIssueId, {
				data,
				error,
				hasNextPage,
				isLoadingNextPage,
				loading,
				loadNextPage,
			});
		}, [
			data,
			error,
			hasNextPage,
			isLoadingNextPage,
			loading,
			loadNextPage,
			onDataChange,
			parentIssueId,
		]);

		return error ? (
			<ErrorFlag error={error} title={messages.errorTitle} description={messages.errorMessage} />
		) : null;
	},
);
